// services/apiClient.js
import { loginRequest } from '../config/config';

/**
 * API Client avec gestion centralisée des tokens MSAL et retry automatique sur 401.
 *
 * Stratégie de token (MSAL v5 + PKCE) :
 *  1. acquireTokenSilent → utilise le cache puis le refresh token (aucun iframe)
 *  2. Si silent échoue → acquireTokenPopup (fonctionne même dans un iframe)
 *  3. Si popup échoue → l'utilisateur doit se reconnecter
 *
 * Sur HTTP 401 : on force un refresh (forceRefresh:true) et on retente UNE fois.
 */
export class ApiClient {
  constructor(msalInstance) {
    if (!msalInstance || typeof msalInstance.getActiveAccount !== 'function') {
      throw new Error('ApiClient: MSAL instance not properly initialized');
    }
    this.msalInstance = msalInstance;
    // Promesse partagée pour dédupliquer les refresh concurrents
    this._refreshPromise = null;
  }

  /**
   * Obtient un token d'accès valide.
   * @param {boolean} forceRefresh - true pour ignorer le cache et forcer un refresh réseau
   * @returns {Promise<string>} accessToken
   */
  async acquireToken(forceRefresh = false) {
    const account = this.msalInstance.getActiveAccount();
    if (!account) {
      throw new Error('No active account. Please log in.');
    }

    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account,
        forceRefresh,
      });
      return response.accessToken;
    } catch (silentError) {
      // Silent a échoué (refresh token expiré, interaction requise, iframe bloqué…)
      // → popup comme fallback universel
      if (process.env.NODE_ENV === 'development') {
        console.warn('acquireTokenSilent failed, trying popup…', silentError?.errorCode || silentError);
      }
      try {
        const popupResponse = await this.msalInstance.acquireTokenPopup({
          scopes: loginRequest.scopes,
          account,
        });
        return popupResponse.accessToken;
      } catch (popupError) {
        if (process.env.NODE_ENV === 'development') {
          console.error('acquireTokenPopup also failed', popupError);
        }
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }
    }
  }

  /**
   * Force un refresh de token, dédupliqué (un seul refresh réseau à la fois).
   * @returns {Promise<string>} nouveau accessToken
   */
  async refreshToken() {
    if (!this._refreshPromise) {
      this._refreshPromise = this.acquireToken(true).finally(() => {
        this._refreshPromise = null;
      });
    }
    return this._refreshPromise;
  }

  /**
   * Effectue un fetch avec headers OData + gestion automatique du 401.
   * @param {string} url
   * @param {string} token - token d'accès courant
   * @param {object} options - options fetch supplémentaires
   * @returns {Promise<Response>}
   */
  async fetchWithRetry(url, token, options = {}) {
    const makeHeaders = (t) => ({
      'OData-MaxVersion': '4.0',
      'OData-Version': '4.0',
      'Accept': 'application/json',
      ...(options.headers || {}),
      'Authorization': `Bearer ${t}`,
    });

    // Premier essai avec le token fourni
    const response = await fetch(url, { ...options, headers: makeHeaders(token) });

    if (response.status === 401) {
      // Token expiré côté serveur → refresh et retry UNE fois
      if (process.env.NODE_ENV === 'development') {
        console.warn('API 401 – refreshing token…');
      }
      const freshToken = await this.refreshToken();
      const retry = await fetch(url, { ...options, headers: makeHeaders(freshToken) });
      if (!retry.ok) {
        throw new Error(`HTTP error after token refresh: ${retry.status}`);
      }
      return retry;
    }

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }
    return response;
  }

  /**
   * Fetch + parse JSON, avec gestion token/401.
   * Si aucun token n'est fourni, en acquiert un automatiquement.
   * @param {string} url
   * @param {string|null} token
   * @param {object} options
   * @returns {Promise<object>}
   */
  async fetchJson(url, token, options = {}) {
    const accessToken = token || (await this.acquireToken());
    const response = await this.fetchWithRetry(url, accessToken, options);
    return response.json();
  }
}

// --- Singleton global ---
let apiClientInstance = null;

export const initializeApiClient = (msalInstance) => {
  apiClientInstance = new ApiClient(msalInstance);
};

export const getApiClient = () => {
  if (!apiClientInstance) {
    throw new Error('API Client not initialized. Call initializeApiClient first.');
  }
  return apiClientInstance;
};
